import type { ReactNode } from 'react';

import styles from './PhoneShell.module.css';

export interface PhoneShellProps {
  readonly children: ReactNode;
  /** The bar at the top: title, back, one action. */
  readonly appBar?: ReactNode;
  /** The four tabs at the bottom. Absent on a focused screen such as an entry sheet. */
  readonly navBar?: ReactNode;
  /**
   * The `+` on `LED-01` and `LED-05`. A slot rather than something a screen positions for itself, because
   * it floats above the nav bar and only the shell knows where that is.
   */
  readonly fab?: ReactNode;
  /** Content that manages its own horizontal edges — a full-bleed list. */
  readonly flush?: boolean;
  /**
   * Lets the content stretch to the height of the shell, so a screen can push its actions to the bottom
   * (`ONB-01`, `ONB-07`). Off by default: a list must not stretch, and a screen that does not ask to fill
   * should keep block layout, where margins collapse the way its CSS expects.
   */
  readonly fill?: boolean;
}

/**
 * The frame every screen in the user app sits in (`LED-01` and everything after it).
 *
 * It owns two things a feature must never have to think about: the safe-area insets, so nothing hides
 * under a notch or a home indicator, and the bottom padding that keeps the last row of a list clear of
 * the nav bar.
 */
export function PhoneShell({
  children,
  appBar,
  navBar,
  fab,
  flush = false,
  fill = false,
}: PhoneShellProps): React.JSX.Element {
  const contentClasses = [
    styles.content,
    flush ? styles.contentFlush : '',
    fill ? styles.contentFill : '',
    navBar ? '' : styles.noNav,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.shell}>
      {appBar}
      <main className={contentClasses}>{children}</main>
      {fab}
      {navBar}
    </div>
  );
}
